import { useCallback } from 'react';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  TextInput,
  useFormShowContext,
} from 'framework/form';
import {
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
  EditableListDataGridLayout,
  useListContext,
} from 'framework/list';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import { defaultsDeep } from 'lodash';
import { getAnchorRowStyle, TextField } from 'framework/ui/field';

const RecipientsList = () => {
  const { isEditMode } = useFormShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { data: { items = [] } = {} } = useListContext();

  const renderActions = useCallback(() => {
    if (isAccessGranted(ACCESS_INFO_SECTIONS.RECIPIENT, ACCESS_INFO_ACTIONS.MANAGE)) {
      return isEditMode
        ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
        : [<EditFormShowButton key="edit" />];
    }
    return null;
  }, [isEditMode, isAccessGranted]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid
          container
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', pl: 2 }}
        >
          <Grid size={2}>
            <Typography variant="h4" color="primary">
              Recipients
            </Typography>
          </Grid>
          <Grid size="auto" alignItems="center" sx={{ '.filter-item-wrapper': { m: 0 } }} />
          <Grid size="grow" />
          <Grid size="auto" display="flex" alignItems="center">
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="textSecondary">No recipients data available</Typography>
        </Box>
      ) : (
        <EditableListDataGridLayout
          rowProps={data =>
            defaultsDeep(getAnchorRowStyle(data, ''), {
              sx: {
                alignItems: 'flex-start',
                py: 2,
              },
            })
          }
          disabledAddNewRow
          getRowContentEnabled={() => true}
          getTableCells={async isEditModeLayout => {
            const getViewModeComponents = (source: string) => {
              return [
                {
                  type: EDITABLE_LIST_NESTED_LIST,
                  source,
                  gridCellProps: { size: 12 },
                  isEditor: true,
                  component: Grid,
                  rows: {
                    emptyListComponent: <Typography>—</Typography>,
                  },
                  componentProps: { direction: 'row', container: true, columnSpacing: 1 },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'firstName',
                      isEditor: false,
                      component: TextField,
                      componentProps: { label: '' },
                      gridCellProps: {
                        size: 3,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'lastName',
                      isEditor: false,
                      component: TextField,
                      componentProps: { label: '' },
                      gridCellProps: {
                        size: 3,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'email',
                      isEditor: false,
                      component: TextField,
                      componentProps: { label: '' },
                      gridCellProps: {
                        size: 6,
                      },
                    },
                  ],
                },
              ];
            };

            const getEditModeComponents = (source: string) => {
              return [
                {
                  type: EDITABLE_LIST_NESTED_LIST,
                  source,
                  gridCellProps: { size: 12 },
                  isEditor: true,
                  component: Grid,
                  rows: {
                    addNewItemButtonText: 'Add recipient',
                    enableUpdateCount: true,
                  },
                  componentProps: { direction: 'row', container: true, columnSpacing: 1 },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'firstName',
                      isEditor: true,
                      component: TextInput,
                      componentProps: { label: 'First Name' },
                      gridCellProps: {
                        size: 3,
                        sx: { '.MuiFormControl-root': { mb: 0 } },
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'lastName',
                      isEditor: true,
                      component: TextInput,
                      componentProps: {
                        label: 'Last Name',
                      },
                      gridCellProps: {
                        size: 3,
                        sx: { '.MuiFormControl-root': { mb: 0 } },
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'email',
                      isEditor: true,
                      component: TextInput,
                      componentProps: {
                        label: 'Email',
                      },
                      gridCellProps: {
                        size: 3,
                        sx: { '.MuiFormControl-root': { mb: 0 } },
                      },
                    },
                  ],
                },
              ];
            };

            return Promise.resolve([
              {
                type: EDITABLE_LIST_GROUP,
                component: Grid,
                componentProps: {
                  container: true,
                },
                gridCellProps: {
                  size: 6,
                },
                children: [
                  {
                    type: EDITABLE_LIST_CUSTOM,
                    component: () => (
                      <Typography color="textSecondary" variant="body2">
                        Main client&#39;s email(s)
                      </Typography>
                    ),
                    gridCellProps: {
                      size: 12,
                      pb: 1,
                      alignItems: 'start',
                    },
                  },
                  ...(isEditMode ? getEditModeComponents('mainRecipients') : getViewModeComponents('mainRecipients')),
                ],
              },
              {
                type: EDITABLE_LIST_GROUP,
                component: Grid,
                componentProps: {
                  container: true,
                },
                gridCellProps: {
                  size: 6,
                },
                children: [
                  {
                    type: EDITABLE_LIST_CUSTOM,
                    component: () => (
                      <Typography color="textSecondary" variant="body2">
                        CC client&#39;s email(s)
                      </Typography>
                    ),
                    gridCellProps: {
                      size: 12,
                      pb: 1,
                      alignItems: 'start',
                    },
                  },
                  ...(isEditMode ? getEditModeComponents('ccRecipients') : getViewModeComponents('ccRecipients')),
                ],
              },
            ]);
          }}
        />
      )}
    </ProjectSectionAccordion>
  );
};

export default RecipientsList;
